/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState, useEffect, useCallback } from 'react';
import {
  CubeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  TrashIcon,
  FolderIcon,
  TagIcon,
  ClockIcon,
  ArrowPathIcon,
  PencilSquareIcon,
  XMarkIcon,
  ChartBarIcon,
  BoltIcon,
  ServerStackIcon,
  CircleStackIcon,
  ShareIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import {
  modelRegistryService,
  datasetService,
  RegisteredModel,
  ModelVersion,
  StageTransition,
  ModelAlias,
  ModelStats,
  ModelLineage,
  normalizeDataset,
} from '../../services/registryApi';
import { useSelectedProject } from '../../hooks/useSelectedProject';
import { useProjects } from '../../hooks/useProjects';
import toast from 'react-hot-toast';

const STAGES = ['development', 'staging', 'production', 'archived'] as const;
type Stage = typeof STAGES[number];

/** Tags from the API may arrive as a JSON string - parse defensively. */
const parseTags = (tags: any): Record<string, string> => {
  if (!tags) return {};
  if (typeof tags === 'string') {
    try { return JSON.parse(tags); } catch { return {}; }
  }
  return tags as Record<string, string>;
};

const GPU_TYPES = ['T4', 'L4', 'A10', 'L40S', 'A100-40G', 'A100-80G', 'H100'] as const;

const StageBadge: React.FC<{ stage: Stage }> = ({ stage }) => {
  const config: Record<Stage, string> = {
    development: 'bg-gray-700 text-gray-300',
    staging: 'bg-yellow-500/20 text-yellow-400',
    production: 'bg-green-500/20 text-green-400',
    archived: 'bg-red-500/20 text-red-400',
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${config[stage]}`}>
      {stage.charAt(0).toUpperCase() + stage.slice(1)}
    </span>
  );
};

const StatusBadge: React.FC<{ status: ModelVersion['status'] }> = ({ status }) => {
  const config: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    ready: 'bg-green-500/20 text-green-400',
    failed: 'bg-red-500/20 text-red-400',
  };

  const labels: Record<string, string> = {
    pending: 'Pending',
    ready: 'Ready',
    failed: 'Failed',
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded ${config[status] || config.pending}`}>
      {labels[status] || status}
    </span>
  );
};

const StatsCard: React.FC<{ title: string; value: number | string; icon: React.ReactNode; color: string }> = ({
  title,
  value,
  icon,
  color,
}) => (
  <div className="bg-gray-900 rounded-xl border border-gray-700 p-4">
    <div className="flex items-center gap-3">
      <div className={`p-2 rounded-lg ${color}`}>{icon}</div>
      <div>
        <p className="text-sm text-gray-400">{title}</p>
        <p className="text-xl font-semibold text-white">{value}</p>
      </div>
    </div>
  </div>
);

// Stage Transition Modal
const StageTransitionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (stage: Stage, comment: string) => void;
  currentStage: Stage;
  version: number;
  loading: boolean;
}> = ({ isOpen, onClose, onConfirm, currentStage, version, loading }) => {
  const [selectedStage, setSelectedStage] = useState<Stage>('staging');
  const [comment, setComment] = useState('');

  const availableStages = STAGES.filter((s) => s !== currentStage);

  useEffect(() => {
    if (isOpen) {
      setSelectedStage(availableStages[0] || 'staging');
      setComment('');
    }
  }, [isOpen, currentStage]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">
            Transition Version {version}
          </h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Current Stage
            </label>
            <StageBadge stage={currentStage} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              New Stage *
            </label>
            <select
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value as Stage)}
              className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
            >
              {availableStages.map((stage) => (
                <option key={stage} value={stage}>
                  {stage.charAt(0).toUpperCase() + stage.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Comment (optional)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
              rows={3}
              placeholder="Reason for transition..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(selectedStage, comment)}
            disabled={loading}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Transitioning...' : 'Confirm Transition'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Alias Modal
const AliasModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (alias: string) => void;
  version: number;
  loading: boolean;
}> = ({ isOpen, onClose, onConfirm, version, loading }) => {
  const [alias, setAlias] = useState('');

  useEffect(() => {
    if (isOpen) setAlias('');
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">
            Set Alias for Version {version}
          </h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Alias Name *
            </label>
            <input
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
              placeholder="e.g., champion, latest, v1-stable"
            />
            <p className="mt-1 text-xs text-gray-400">
              Aliases like &quot;champion&quot; or &quot;challenger&quot; help track important versions
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(alias)}
            disabled={loading || !alias.trim()}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Setting...' : 'Set Alias'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  loading: boolean;
}> = ({ isOpen, onClose, onConfirm, title, message, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-500/20 rounded-full">
            <TrashIcon className="w-6 h-6 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white">
            {title}
          </h2>
        </div>

        <p className="text-gray-300 mb-6">
          {message}
        </p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Transitions History Panel
const TransitionsPanel: React.FC<{
  transitions: StageTransition[];
  loading: boolean;
}> = ({ transitions, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="animate-spin rounded-full h-5 w-5 border-gray-700 border-t-primary-500"></div>
      </div>
    );
  }

  if (transitions.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-2">No transitions recorded</p>
    );
  }

  return (
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {transitions.map((t) => (
        <div key={t.id} className="flex items-start gap-2 text-sm">
          <ClockIcon className="w-4 h-4 text-gray-600 mt-0.5" />
          <div>
            <span className="text-gray-300">
              v{t.version}: {t.from_stage || 'new'} → {t.to_stage}
            </span>
            {t.comment && (
              <p className="text-xs text-gray-400 mt-0.5">{t.comment}</p>
            )}
            <p className="text-xs text-gray-400">
              {new Date(t.created_at).toLocaleString()}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Add Version Modal
const AddVersionModal: React.FC<{
  modelName: string;
  onClose: () => void;
  onConfirm: (runId: string, description: string) => void;
  loading: boolean;
}> = ({ modelName, onClose, onConfirm, loading }) => {
  const [runId, setRunId] = useState('');
  const [description, setDescription] = useState('');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Add Version - {modelName}</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-300"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Run ID (optional)</label>
            <input
              type="text" value={runId} onChange={(e) => setRunId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
              placeholder="Link to a training run ID"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Description (optional)</label>
            <textarea
              value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
              className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
              placeholder="What changed in this version?"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors">Cancel</button>
          <button
            onClick={() => onConfirm(runId.trim(), description.trim())}
            disabled={loading}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Version'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Download / Get Model Modal
const DownloadModal: React.FC<{
  modelName: string;
  version: number;
  stage: string;
  source: string;
  onClose: () => void;
}> = ({ modelName, version, stage, source, onClose }) => {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const byVersion = `from podstack import registry\n\nregistry.init(api_key="...", project_id="...")\nmodel = registry.load_model("${modelName}", version=${version})`;
  const byStage = stage !== 'archived'
    ? `from podstack import registry\n\nregistry.init(api_key="...", project_id="...")\nmodel = registry.load_model("${modelName}", stage="${stage}")`
    : null;

  const CodeBlock: React.FC<{ code: string; id: string; label: string }> = ({ code, id, label }) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-400">{label}</span>
        <button
          onClick={() => copy(code, id)}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
        >
          {copied === id
            ? <><CheckIcon className="w-3.5 h-3.5 text-green-400" /><span className="text-green-400">Copied</span></>
            : <><ClipboardDocumentIcon className="w-3.5 h-3.5" />Copy</>}
        </button>
      </div>
      <pre className="bg-gray-950 border border-gray-700 rounded-lg p-3 text-xs font-mono text-gray-200 overflow-x-auto whitespace-pre">
        {code}
      </pre>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-lg mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <ArrowDownTrayIcon className="w-5 h-5 text-primary-400" />
              Download {modelName} v{version}
            </h2>
            <p className="text-sm text-gray-400 mt-0.5">Load this model locally using the Podstack SDK</p>
          </div>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Install */}
          <CodeBlock
            id="install"
            label="1 · Install the SDK"
            code="pip install podstack"
          />

          {/* Load by version */}
          <CodeBlock
            id="by-version"
            label={`2 · Load v${version} by version number`}
            code={byVersion}
          />

          {/* Load by stage (if not archived) */}
          {byStage && (
            <CodeBlock
              id="by-stage"
              label={`or · Load by stage (${stage})`}
              code={byStage}
            />
          )}

          {/* Artifact source path */}
          {source && (
            <div className="flex items-start gap-2 bg-gray-800/60 rounded-lg px-3 py-2.5 text-xs">
              <FolderIcon className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-gray-400 mb-0.5">Artifact source</p>
                <p className="font-mono text-gray-300 break-all">{source}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Deploy Modal
const DeployModal: React.FC<{
  modelName: string;
  version: number;
  onClose: () => void;
  onConfirm: (config: DeployConfig) => void;
  loading: boolean;
}> = ({ modelName, version, onClose, onConfirm, loading }) => {
  const [config, setConfig] = useState<DeployConfig>({
    endpointName: `${modelName}-v${version}`.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
    gpuType: 'L40S',
    minReplicas: 1,
    maxReplicas: 3,
    framework: 'pytorch',
    timeoutSeconds: 120,
  });
  const set = (k: keyof DeployConfig, v: any) => setConfig(prev => ({ ...prev, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-lg mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <BoltIcon className="w-5 h-5 text-yellow-400" />
              Deploy {modelName} v{version}
            </h2>
            <p className="text-sm text-gray-400 mt-0.5">Launch a serverless inference endpoint</p>
          </div>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-300"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Endpoint Name *</label>
            <input
              type="text" value={config.endpointName} onChange={(e) => set('endpointName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">GPU Type</label>
              <select
                value={config.gpuType} onChange={(e) => set('gpuType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
              >
                {GPU_TYPES.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Framework</label>
              <select
                value={config.framework} onChange={(e) => set('framework', e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
              >
                {['pytorch', 'tensorflow', 'sklearn', 'onnx', 'huggingface'].map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Min Replicas</label>
              <input
                type="number" min={0} max={10} value={config.minReplicas}
                onChange={(e) => set('minReplicas', Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Max Replicas</label>
              <input
                type="number" min={1} max={20} value={config.maxReplicas}
                onChange={(e) => set('maxReplicas', Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Timeout (s)</label>
              <input
                type="number" min={30} max={3600} value={config.timeoutSeconds}
                onChange={(e) => set('timeoutSeconds', Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-3 text-xs text-gray-400 space-y-1">
            <p className="flex items-center gap-2"><ServerStackIcon className="w-4 h-4 text-primary-400" /> Serverless GPU endpoint - scales to zero when idle</p>
            <p>Cold start: ~15–30s &nbsp;·&nbsp; Billed per request</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors">Cancel</button>
          <button
            onClick={() => onConfirm(config)}
            disabled={loading || !config.endpointName.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <BoltIcon className="w-4 h-4" />
            {loading ? 'Deploying...' : 'Deploy Endpoint'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface DeployConfig {
  endpointName: string;
  gpuType: string;
  minReplicas: number;
  maxReplicas: number;
  framework: string;
  timeoutSeconds: number;
}

const ModelRegistry: React.FC = () => {
  const {
    selectedProjectId,
    setSelectedProject,
    autoSelectFirstProject,
    projects,
    projectsLoading,
  } = useSelectedProject();
  const { createProject, isCreating: isCreatingProject } = useProjects();
  const [models, setModels] = useState<RegisteredModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedModelId, setExpandedModelId] = useState<string | null>(null);
  const [versions, setVersions] = useState<Record<string, ModelVersion[]>>({});
  const [versionsLoading, setVersionsLoading] = useState<Record<string, boolean>>({});
  const [aliases, setAliases] = useState<Record<string, ModelAlias[]>>({});
  const [transitions, setTransitions] = useState<Record<string, StageTransition[]>>({});
  const [transitionsLoading, setTransitionsLoading] = useState<Record<string, boolean>>({});
  const [stats, setStats] = useState<ModelStats | null>(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newModel, setNewModel] = useState({ name: '', description: '', projectId: '' });
  const [creating, setCreating] = useState(false);
  const [showNewProjectForm, setShowNewProjectForm] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });

  // Transition modal state
  const [transitionModal, setTransitionModal] = useState<{
    isOpen: boolean;
    modelId: string;
    version: ModelVersion | null;
  }>({ isOpen: false, modelId: '', version: null });
  const [transitioning, setTransitioning] = useState(false);

  // Alias modal state
  const [aliasModal, setAliasModal] = useState<{
    isOpen: boolean;
    modelId: string;
    version: number;
  }>({ isOpen: false, modelId: '', version: 0 });
  const [settingAlias, setSettingAlias] = useState(false);

  // Show transitions panel
  const [showTransitions, setShowTransitions] = useState<string | null>(null);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    type: 'model' | 'version';
    modelId: string;
    modelName: string;
    version?: number;
  }>({ isOpen: false, type: 'model', modelId: '', modelName: '' });
  const [deleting, setDeleting] = useState(false);

  // Add Version modal state
  const [addVersionModal, setAddVersionModal] = useState<{
    isOpen: boolean;
    modelId: string;
    modelName: string;
  }>({ isOpen: false, modelId: '', modelName: '' });
  const [addingVersion, setAddingVersion] = useState(false);

  // Deploy modal state
  const [deployModal, setDeployModal] = useState<{
    isOpen: boolean;
    modelId: string;
    modelName: string;
    version: number;
  }>({ isOpen: false, modelId: '', modelName: '', version: 0 });
  const [deploying, setDeploying] = useState(false);

  // Download modal state
  const [downloadModal, setDownloadModal] = useState<{
    isOpen: boolean;
    modelName: string;
    version: number;
    stage: string;
    source: string;
  }>({ isOpen: false, modelName: '', version: 0, stage: '', source: '' });

  // Stage filter per model (empty string = "All")
  const [versionStageFilter, setVersionStageFilter] = useState<Record<string, string>>({});

  // Lineage tab state per model
  const [modelViewTab, setModelViewTab] = useState<Record<string, 'versions' | 'lineage'>>({});
  const [lineage, setLineage] = useState<Record<string, ModelLineage>>({});
  const [lineageLoading, setLineageLoading] = useState<Record<string, boolean>>({});

  // Auto-select first project when projects load and none is selected
  useEffect(() => {
    if (!projectsLoading) autoSelectFirstProject();
  }, [projectsLoading, autoSelectFirstProject]);

  // Keep create-modal project in sync with the globally selected project
  useEffect(() => {
    setNewModel((prev) => ({ ...prev, projectId: selectedProjectId }));
  }, [selectedProjectId]);

  const handleProjectChange = (projectId: string) => {
    setSelectedProject(projectId);
    localStorage.setItem('selectedProjectId', projectId);
  };

  const fetchModels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await modelRegistryService.list({ limit: 100 });
      setModels(response.data.models || []);
    } catch (error) {
      console.error('Failed to fetch models:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await modelRegistryService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  const fetchVersions = async (modelId: string) => {
    try {
      setVersionsLoading((prev) => ({ ...prev, [modelId]: true }));
      const response = await modelRegistryService.listVersions(modelId);
      setVersions((prev) => ({ ...prev, [modelId]: response.data.versions || [] }));
    } catch (error) {
      console.error('Failed to fetch versions:', error);
    } finally {
      setVersionsLoading((prev) => ({ ...prev, [modelId]: false }));
    }
  };

  const fetchLineage = async (modelId: string) => {
    if (lineage[modelId]) return; // already loaded
    try {
      setLineageLoading((prev) => ({ ...prev, [modelId]: true }));
      const response = await datasetService.getModelLineage(modelId);
      const data = response.data as ModelLineage;
      // Normalize dataset JSON fields
      const normalized: ModelLineage = {
        ...data,
        versions: (data.versions || []).map((v) => ({
          ...v,
          datasets: (v.datasets || []).map(normalizeDataset),
        })),
      };
      setLineage((prev) => ({ ...prev, [modelId]: normalized }));
    } catch (error) {
      console.error('Failed to fetch lineage:', error);
    } finally {
      setLineageLoading((prev) => ({ ...prev, [modelId]: false }));
    }
  };

  const fetchAliases = async (modelId: string) => {
    try {
      const response = await modelRegistryService.getAliases(modelId);
      setAliases((prev) => ({ ...prev, [modelId]: response.data.aliases || [] }));
    } catch (error) {
      console.error('Failed to fetch aliases:', error);
    }
  };

  const fetchModelTransitions = async (modelId: string) => {
    try {
      setTransitionsLoading((prev) => ({ ...prev, [modelId]: true }));
      const response = await modelRegistryService.getModelTransitions(modelId);
      setTransitions((prev) => ({ ...prev, [modelId]: response.data.transitions || [] }));
    } catch (error) {
      console.error('Failed to fetch transitions:', error);
    } finally {
      setTransitionsLoading((prev) => ({ ...prev, [modelId]: false }));
    }
  };

  useEffect(() => {
    if (selectedProjectId) {
      fetchModels();
      fetchStats();
    }
  }, [fetchModels, fetchStats, selectedProjectId]);

  const handleToggleExpand = (modelId: string) => {
    if (expandedModelId === modelId) {
      setExpandedModelId(null);
      setShowTransitions(null);
    } else {
      setExpandedModelId(modelId);
      if (!versions[modelId]) {
        fetchVersions(modelId);
      }
      if (!aliases[modelId]) {
        fetchAliases(modelId);
      }
    }
  };

  const handleOpenTransitionModal = (modelId: string, version: ModelVersion) => {
    setTransitionModal({ isOpen: true, modelId, version });
  };

  const handleTransitionStage = async (stage: Stage, comment: string) => {
    if (!transitionModal.version) return;

    try {
      setTransitioning(true);
      await modelRegistryService.transitionStage(
        transitionModal.modelId,
        transitionModal.version.version,
        stage,
        comment
      );
      toast.success(`Version ${transitionModal.version.version} transitioned to ${stage}`);
      setTransitionModal({ isOpen: false, modelId: '', version: null });
      fetchVersions(transitionModal.modelId);
      fetchModelTransitions(transitionModal.modelId);
      fetchStats();
    } catch (error) {
      console.error('Failed to transition stage:', error);
    } finally {
      setTransitioning(false);
    }
  };

  const handleOpenAliasModal = (modelId: string, version: number) => {
    setAliasModal({ isOpen: true, modelId, version });
  };

  const handleSetAlias = async (alias: string) => {
    try {
      setSettingAlias(true);
      await modelRegistryService.setAlias(aliasModal.modelId, {
        alias,
        version: aliasModal.version,
      });
      toast.success(`Alias "${alias}" set for version ${aliasModal.version}`);
      setAliasModal({ isOpen: false, modelId: '', version: 0 });
      fetchAliases(aliasModal.modelId);
    } catch (error) {
      console.error('Failed to set alias:', error);
    } finally {
      setSettingAlias(false);
    }
  };

  const handleDeleteAlias = async (modelId: string, alias: string) => {
    if (!window.confirm(`Delete alias "${alias}"?`)) return;

    try {
      await modelRegistryService.deleteAlias(modelId, alias);
      toast.success(`Alias "${alias}" deleted`);
      fetchAliases(modelId);
    } catch (error) {
      console.error('Failed to delete alias:', error);
    }
  };

  const handleToggleTransitions = (modelId: string) => {
    if (showTransitions === modelId) {
      setShowTransitions(null);
    } else {
      setShowTransitions(modelId);
      if (!transitions[modelId]) {
        fetchModelTransitions(modelId);
      }
    }
  };

  const handleCreate = async () => {
    if (!newModel.name.trim()) {
      toast.error('Model name is required');
      return;
    }
    if (!newModel.projectId) {
      toast.error('Please select a project');
      return;
    }

    try {
      setCreating(true);
      await modelRegistryService.create({
        name: newModel.name,
        description: newModel.description,
        project_id: newModel.projectId,
      });
      toast.success('Model registered successfully');

      // If user picked a different project in the modal, persist that choice globally
      if (newModel.projectId && newModel.projectId !== selectedProjectId) {
        setSelectedProject(newModel.projectId);
        localStorage.setItem('selectedProjectId', newModel.projectId);
      }

      setShowCreateModal(false);
      setNewModel({ name: '', description: '', projectId: newModel.projectId });
      setShowNewProjectForm(false);
      setNewProject({ name: '', description: '' });
      fetchModels();
      fetchStats();
    } catch (error) {
      console.error('Failed to create model:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateProject = () => {
    if (!newProject.name.trim()) {
      toast.error('Project name is required');
      return;
    }
    createProject(
      { name: newProject.name, description: newProject.description },
      {
        onSuccess: (response: any) => {
          const createdProject = response?.data?.project;
          if (createdProject?.id) {
            setNewModel((prev) => ({ ...prev, projectId: createdProject.id }));
          }
          setShowNewProjectForm(false);
          setNewProject({ name: '', description: '' });
        },
      }
    );
  };

  const openDeleteModal = (modelId: string, modelName: string, version?: number) => {
    setDeleteModal({
      isOpen: true,
      type: version !== undefined ? 'version' : 'model',
      modelId,
      modelName,
      version,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, type: 'model', modelId: '', modelName: '' });
  };

  const handleConfirmDelete = async () => {
    const { type, modelId, version } = deleteModal;

    try {
      setDeleting(true);
      if (type === 'version' && version !== undefined) {
        await modelRegistryService.deleteVersion(modelId, version);
        toast.success(`Version ${version} deleted`);
        fetchVersions(modelId);
      } else {
        await modelRegistryService.delete(modelId);
        toast.success('Model deleted');
      }
      fetchModels();
      fetchStats();
      closeDeleteModal();
    } catch (error) {
      console.error('Failed to delete:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleAddVersion = async (runId: string, description: string) => {
    try {
      setAddingVersion(true);
      await modelRegistryService.createVersion(addVersionModal.modelId, {
        run_id: runId || undefined,
        description: description || undefined,
      });
      toast.success('Version created successfully');
      const mid = addVersionModal.modelId;
      setAddVersionModal({ isOpen: false, modelId: '', modelName: '' });
      fetchVersions(mid);
      fetchModels();
      fetchStats();
    } catch (error) {
      console.error('Failed to create version:', error);
    } finally {
      setAddingVersion(false);
    }
  };

  const handleDeploy = (_config: DeployConfig) => {
    toast('Serverless deployment coming soon! Endpoint configuration saved.', { icon: '🚀' });
    setDeployModal({ isOpen: false, modelId: '', modelName: '', version: 0 });
    setDeploying(false);
  };

  const filteredModels = models.filter(
    (model) =>
      model.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      model.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getAliasesForVersion = (modelId: string, version: number): ModelAlias[] => {
    return (aliases[modelId] || []).filter((a) => a.version === version);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white">Model Registry</h1>
            <p className="mt-1 text-sm text-gray-400">
              Manage and deploy your trained ML models
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Project Selector */}
            <div className="relative">
              <FolderIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={selectedProjectId}
                onChange={(e) => handleProjectChange(e.target.value)}
                disabled={projectsLoading}
                className="pl-9 pr-8 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white text-sm focus:ring-2 focus:ring-primary-500 appearance-none cursor-pointer disabled:opacity-50"
              >
                {projects.length === 0 && <option value="">No projects</option>}
                {projects.map((p: any) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              Register Model
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatsCard
              title="Total Models"
              value={stats.total_models}
              icon={<CubeIcon className="w-5 h-5 text-primary-500" />}
              color="bg-primary-500/20"
            />
            <StatsCard
              title="Total Versions"
              value={stats.total_versions}
              icon={<ChartBarIcon className="w-5 h-5 text-purple-500" />}
              color="bg-purple-500/20"
            />
            <StatsCard
              title="In Production"
              value={stats.versions_by_stage?.production || 0}
              icon={<CubeIcon className="w-5 h-5 text-green-500" />}
              color="bg-green-500/20"
            />
            <StatsCard
              title="In Staging"
              value={stats.versions_by_stage?.staging || 0}
              icon={<CubeIcon className="w-5 h-5 text-yellow-500" />}
              color="bg-yellow-500/20"
            />
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search models..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Models List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-gray-700 border-t-primary-500"></div>
          </div>
        ) : filteredModels.length === 0 ? (
          <div className="text-center py-12 bg-gray-900 rounded-xl border border-gray-700">
            <CubeIcon className="mx-auto h-12 w-12 text-gray-600" />
            <h3 className="mt-2 text-sm font-medium text-white">No models</h3>
            <p className="mt-1 text-sm text-gray-400">
              Register your first model to get started with model versioning and deployment.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              Register Model
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredModels.map((model) => (
              <div
                key={model.id}
                className="bg-gray-900 rounded-xl border border-gray-700 overflow-hidden"
              >
                {/* Model Header */}
                <div
                  onClick={() => handleToggleExpand(model.id)}
                  className="px-6 py-4 cursor-pointer hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CubeIcon className="w-6 h-6 text-primary-500" />
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          {model.name}
                        </h3>
                        {model.description && (
                          <p className="text-sm text-gray-400">
                            {model.description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-400">
                        {model.version_count} version{model.version_count !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddVersionModal({ isOpen: true, modelId: model.id, modelName: model.name });
                        }}
                        className="p-1 text-gray-600 hover:text-green-400 rounded"
                        title="Add new version"
                      >
                        <PlusIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleTransitions(model.id);
                        }}
                        className="p-1 text-gray-600 hover:text-primary-500 rounded"
                        title="View transition history"
                      >
                        <ClockIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteModal(model.id, model.name);
                        }}
                        className="p-1 text-gray-600 hover:text-red-500 rounded"
                        title="Delete model"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                      {expandedModelId === model.id ? (
                        <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                      ) : (
                        <ChevronRightIcon className="w-5 h-5 text-gray-600" />
                      )}
                    </div>
                  </div>

                  {Object.keys(parseTags(model.tags)).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {Object.entries(parseTags(model.tags)).map(([key, value]) => (
                        <span
                          key={key}
                          className="px-2 py-0.5 text-xs bg-gray-700 text-gray-300 rounded"
                        >
                          {key}: {value as string}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Aliases display */}
                  {aliases[model.id] && aliases[model.id].length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {aliases[model.id].map((alias) => (
                        <span
                          key={alias.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary-500/20 text-primary-400 rounded"
                        >
                          <TagIcon className="w-3 h-3" />
                          {alias.alias} → v{alias.version}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Transitions Panel */}
                {showTransitions === model.id && (
                  <div className="px-6 py-4 border-t border-gray-700 bg-gray-800">
                    <h4 className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                      <ArrowPathIcon className="w-4 h-4" />
                      Stage Transition History
                    </h4>
                    <TransitionsPanel
                      transitions={transitions[model.id] || []}
                      loading={transitionsLoading[model.id] || false}
                    />
                  </div>
                )}

                {/* Expanded Versions */}
                {expandedModelId === model.id && (
                  <div className="border-t border-gray-700">
                    {/* View tab switcher (Versions | Lineage) */}
                    <div className="flex items-center gap-4 px-6 pt-3 pb-0 border-b border-gray-700 bg-gray-800/50">
                      <button
                        onClick={() => setModelViewTab((prev) => ({ ...prev, [model.id]: 'versions' }))}
                        className={`py-2 text-sm font-medium border-b-2 transition-colors ${
                          (modelViewTab[model.id] || 'versions') === 'versions'
                            ? 'border-primary-500 text-primary-400'
                            : 'border-transparent text-gray-400 hover:text-gray-200'
                        }`}
                      >
                        Versions
                      </button>
                      <button
                        onClick={() => {
                          setModelViewTab((prev) => ({ ...prev, [model.id]: 'lineage' }));
                          fetchLineage(model.id);
                        }}
                        className={`py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                          modelViewTab[model.id] === 'lineage'
                            ? 'border-primary-500 text-primary-400'
                            : 'border-transparent text-gray-400 hover:text-gray-200'
                        }`}
                      >
                        <ShareIcon className="w-3.5 h-3.5" />
                        Lineage
                      </button>
                    </div>

                    {/* Lineage View */}
                    {modelViewTab[model.id] === 'lineage' && (
                      <div className="p-6">
                        {lineageLoading[model.id] ? (
                          <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                          </div>
                        ) : !lineage[model.id] || lineage[model.id].versions.length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            <ShareIcon className="mx-auto h-10 w-10 text-gray-600 mb-2" />
                            <p className="text-sm">No lineage data yet.</p>
                            <p className="text-xs mt-1">Register a model version linked to a run, then call <code className="font-mono bg-gray-800 px-1 rounded">run.log_dataset()</code>.</p>
                          </div>
                        ) : (
                          <div className="space-y-6">
                            {lineage[model.id].versions.map((vl) => (
                              <div key={vl.version} className="flex items-start gap-3 flex-wrap">
                                {/* Datasets column */}
                                <div className="flex flex-col gap-2 min-w-0">
                                  {vl.datasets.length === 0 ? (
                                    <div className="px-3 py-2 rounded-lg border border-dashed border-gray-600 text-xs text-gray-500">
                                      No datasets
                                    </div>
                                  ) : (
                                    vl.datasets.map((ds) => (
                                      <div key={ds.id} className="px-3 py-2 rounded-lg border border-blue-500/30 bg-blue-500/10 max-w-xs">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                          <CircleStackIcon className="w-3.5 h-3.5 text-blue-400 shrink-0" />
                                          <span className="text-xs font-medium text-blue-300 truncate">{ds.name}</span>
                                        </div>
                                        {ds.digest && (
                                          <div className="text-xs text-gray-500 font-mono truncate">
                                            {ds.digest.slice(0, 12)}…
                                          </div>
                                        )}
                                        {ds.profile?.num_rows !== undefined && (
                                          <div className="text-xs text-gray-500">{ds.profile.num_rows.toLocaleString()} rows</div>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>

                                {/* Arrow */}
                                <div className="flex items-center self-center text-gray-500 text-sm mx-1">→</div>

                                {/* Run node */}
                                {vl.run_id ? (
                                  <div className="px-3 py-2 rounded-lg border border-gray-600 bg-gray-800 max-w-xs">
                                    <div className="text-xs font-medium text-gray-300 truncate">
                                      {vl.run_name || `Run ${vl.run_id.slice(0, 8)}`}
                                    </div>
                                    <div className="text-xs text-gray-500 font-mono">{vl.run_id.slice(0, 8)}…</div>
                                  </div>
                                ) : (
                                  <div className="px-3 py-2 rounded-lg border border-dashed border-gray-600 text-xs text-gray-500">
                                    No run linked
                                  </div>
                                )}

                                {/* Arrow */}
                                <div className="flex items-center self-center text-gray-500 text-sm mx-1">→</div>

                                {/* Version node */}
                                <div className={`px-3 py-2 rounded-lg border max-w-xs ${
                                  vl.stage === 'production' ? 'border-green-500/40 bg-green-500/10' :
                                  vl.stage === 'staging' ? 'border-yellow-500/40 bg-yellow-500/10' :
                                  'border-gray-600 bg-gray-800'
                                }`}>
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-xs font-semibold text-white">v{vl.version}</span>
                                    <StageBadge stage={vl.stage as any} />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Stage filter tabs (only shown in versions view) */}
                    {(modelViewTab[model.id] || 'versions') === 'versions' && !versionsLoading[model.id] && versions[model.id] && versions[model.id].length > 0 && (
                      <div className="flex items-center gap-1 px-6 pt-3 pb-0 border-b border-gray-700 bg-gray-800/30">
                        {['', ...STAGES].map((s) => {
                          const label = s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1);
                          const count = s === '' ? versions[model.id].length : versions[model.id].filter(v => v.stage === s).length;
                          const active = (versionStageFilter[model.id] || '') === s;
                          return (
                            <button
                              key={s || 'all'}
                              onClick={() => setVersionStageFilter(prev => ({ ...prev, [model.id]: s }))}
                              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                                active
                                  ? 'border-primary-500 text-primary-400'
                                  : 'border-transparent text-gray-400 hover:text-gray-200'
                              }`}
                            >
                              {label}
                              {count > 0 && (
                                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-primary-500/20 text-primary-400' : 'bg-gray-700 text-gray-400'}`}>
                                  {count}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {(modelViewTab[model.id] || 'versions') === 'versions' && versionsLoading[model.id] ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-gray-700 border-t-primary-500"></div>
                      </div>
                    ) : (modelViewTab[model.id] || 'versions') === 'versions' && (!versions[model.id] || versions[model.id].length === 0) ? (
                      <div className="text-center py-8 text-gray-400">
                        <p>No versions found.</p>
                        <button
                          onClick={() => setAddVersionModal({ isOpen: true, modelId: model.id, modelName: model.name })}
                          className="mt-2 inline-flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300"
                        >
                          <PlusIcon className="w-4 h-4" /> Add first version
                        </button>
                      </div>
                    ) : (modelViewTab[model.id] || 'versions') === 'versions' ? (() => {
                      const stageFilter = versionStageFilter[model.id] || '';
                      const displayVersions = stageFilter
                        ? versions[model.id].filter(v => v.stage === stageFilter)
                        : versions[model.id];
                      return displayVersions.length === 0 ? (
                        <div className="text-center py-8 text-gray-400">
                          No versions in {stageFilter} stage.
                        </div>
                      ) : (
                      <table className="min-w-full">
                        <thead className="bg-gray-800">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                              Version
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                              Stage
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                              Status
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                              Aliases
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">
                              Created
                            </th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                          {displayVersions.map((version) => {
                            const versionAliases = getAliasesForVersion(model.id, version.version);
                            return (
                              <tr key={version.id} className="hover:bg-gray-800">
                                <td className="px-6 py-4">
                                  <span className="text-sm font-medium text-white">
                                    v{version.version}
                                  </span>
                                  {version.description && (
                                    <p className="text-xs text-gray-400 mt-0.5 max-w-xs truncate">
                                      {version.description}
                                    </p>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <StageBadge stage={version.stage} />
                                </td>
                                <td className="px-6 py-4">
                                  <StatusBadge status={version.status} />
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex flex-wrap gap-1">
                                    {versionAliases.map((alias) => (
                                      <span
                                        key={alias.id}
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-primary-500/20 text-primary-400 rounded group"
                                      >
                                        <TagIcon className="w-3 h-3" />
                                        {alias.alias}
                                        <button
                                          onClick={() => handleDeleteAlias(model.id, alias.alias)}
                                          className="opacity-0 group-hover:opacity-100 ml-0.5 text-primary-500 hover:text-red-500"
                                        >
                                          <XMarkIcon className="w-3 h-3" />
                                        </button>
                                      </span>
                                    ))}
                                    {versionAliases.length === 0 && (
                                      <span className="text-xs text-gray-400">-</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-300">
                                  {new Date(version.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 text-right">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      onClick={() => setDownloadModal({ isOpen: true, modelName: model.name, version: version.version, stage: version.stage, source: version.source || '' })}
                                      className="p-1.5 text-gray-400 hover:text-primary-400 rounded hover:bg-primary-500/20"
                                      title="Download / use model locally"
                                    >
                                      <ArrowDownTrayIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => setDeployModal({ isOpen: true, modelId: model.id, modelName: model.name, version: version.version })}
                                      className="p-1.5 text-gray-400 hover:text-yellow-400 rounded hover:bg-yellow-500/20"
                                      title="Deploy as serverless endpoint"
                                    >
                                      <BoltIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleOpenTransitionModal(model.id, version)}
                                      className="p-1.5 text-gray-400 hover:text-primary-500 rounded hover:bg-primary-500/20"
                                      title="Transition stage"
                                    >
                                      <ArrowPathIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleOpenAliasModal(model.id, version.version)}
                                      className="p-1.5 text-gray-400 hover:text-purple-500 rounded hover:bg-purple-500/20"
                                      title="Set alias"
                                    >
                                      <TagIcon className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => openDeleteModal(model.id, model.name, version.version)}
                                      className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-500/20"
                                      title="Delete version"
                                    >
                                      <TrashIcon className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      );
                    })() : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
              <h2 className="text-xl font-semibold text-white mb-4">
                Register New Model
              </h2>
              <div className="space-y-4">
                {/* Project Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Project *
                  </label>
                  {!showNewProjectForm ? (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <FolderIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                          <select
                            value={newModel.projectId}
                            onChange={(e) => setNewModel({ ...newModel, projectId: e.target.value })}
                            className="w-full pl-10 pr-10 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500 appearance-none"
                            disabled={projectsLoading}
                          >
                            <option value="">Select a project</option>
                            {projects.map((project: any) => (
                              <option key={project.id} value={project.id}>
                                {project.name}
                              </option>
                            ))}
                          </select>
                          <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowNewProjectForm(true)}
                          className="px-3 py-2 text-sm font-medium text-primary-500 hover:text-primary-400 border border-primary-500 hover:border-primary-400 rounded-lg transition-colors whitespace-nowrap"
                        >
                          <PlusIcon className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3 p-3 border border-gray-700 rounded-lg bg-gray-800">
                      <p className="text-sm font-medium text-gray-300">
                        Create New Project
                      </p>
                      <input
                        type="text"
                        value={newProject.name}
                        onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-primary-500 text-sm"
                        placeholder="Project name"
                      />
                      <input
                        type="text"
                        value={newProject.description}
                        onChange={(e) =>
                          setNewProject({ ...newProject, description: e.target.value })
                        }
                        className="w-full px-3 py-2 border border-gray-600 rounded-lg bg-gray-700 text-white focus:ring-2 focus:ring-primary-500 text-sm"
                        placeholder="Project description (optional)"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowNewProjectForm(false);
                            setNewProject({ name: '', description: '' });
                          }}
                          className="px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-700 rounded transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCreateProject}
                          disabled={isCreatingProject || !newProject.name.trim()}
                          className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:opacity-50"
                        >
                          {isCreatingProject ? 'Creating...' : 'Create Project'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Model Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Model Name *
                  </label>
                  <input
                    type="text"
                    value={newModel.name}
                    onChange={(e) => setNewModel({ ...newModel, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
                    placeholder="my-model"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    value={newModel.description}
                    onChange={(e) => setNewModel({ ...newModel, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-primary-500"
                    rows={3}
                    placeholder="Model description..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowNewProjectForm(false);
                    setNewProject({ name: '', description: '' });
                  }}
                  className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating || showNewProjectForm}
                  className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {creating ? 'Registering...' : 'Register Model'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stage Transition Modal */}
        <StageTransitionModal
          isOpen={transitionModal.isOpen}
          onClose={() => setTransitionModal({ isOpen: false, modelId: '', version: null })}
          onConfirm={handleTransitionStage}
          currentStage={transitionModal.version?.stage || 'development'}
          version={transitionModal.version?.version || 0}
          loading={transitioning}
        />

        {/* Alias Modal */}
        <AliasModal
          isOpen={aliasModal.isOpen}
          onClose={() => setAliasModal({ isOpen: false, modelId: '', version: 0 })}
          onConfirm={handleSetAlias}
          version={aliasModal.version}
          loading={settingAlias}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={deleteModal.isOpen}
          onClose={closeDeleteModal}
          onConfirm={handleConfirmDelete}
          title={deleteModal.type === 'version' ? 'Delete Version' : 'Delete Model'}
          message={
            deleteModal.type === 'version'
              ? `Are you sure you want to delete version ${deleteModal.version} of "${deleteModal.modelName}"? This action cannot be undone.`
              : `Are you sure you want to delete the model "${deleteModal.modelName}"? This action cannot be undone.`
          }
          loading={deleting}
        />

        {/* Add Version Modal */}
        {addVersionModal.isOpen && (
          <AddVersionModal
            modelName={addVersionModal.modelName}
            onClose={() => setAddVersionModal({ isOpen: false, modelId: '', modelName: '' })}
            onConfirm={handleAddVersion}
            loading={addingVersion}
          />
        )}

        {/* Download Modal */}
        {downloadModal.isOpen && (
          <DownloadModal
            modelName={downloadModal.modelName}
            version={downloadModal.version}
            stage={downloadModal.stage}
            source={downloadModal.source}
            onClose={() => setDownloadModal({ isOpen: false, modelName: '', version: 0, stage: '', source: '' })}
          />
        )}

        {/* Deploy Modal */}
        {deployModal.isOpen && (
          <DeployModal
            modelName={deployModal.modelName}
            version={deployModal.version}
            onClose={() => setDeployModal({ isOpen: false, modelId: '', modelName: '', version: 0 })}
            onConfirm={handleDeploy}
            loading={deploying}
          />
        )}
      </div>
    </div>
  );
};

export default ModelRegistry;
